/** Keeps an editor toolbar in the browser top layer without moving its DOM or listeners. */
export class FloatingRichTextToolbar {
  private _open = false;
  private _frame = 0;
  private _pendingPointer = false;
  private _exitTimer?: number;
  private _observer: ResizeObserver | null = null;

  constructor(private readonly _owner: HTMLElement, private readonly _toolbar: HTMLElement,
    private readonly _anchor: HTMLElement, private readonly _onExit: () => void = () => {}) {
    _toolbar.setAttribute('popover', 'manual');
    _owner.addEventListener('focusin', this.show);
    _owner.addEventListener('focusout', this._onFocusOut);
    _owner.addEventListener('keydown', this._onKeyDown);
  }

  containsFocus(): boolean { return containsComposed(this._owner, deepActiveElement()); }

  containsInteraction(): boolean {
    if (this._pendingPointer || this.containsFocus()) return true;
    // Pickers rebuild their own DOM as choices change. Focus temporarily falls
    // back to body while their modal remains open; that is still one edit.
    return deepActiveElement() === document.body && Array.from(
      this._toolbar.querySelectorAll('qds-font-picker, qds-color-picker')
    ).some(picker => picker.shadowRoot?.querySelector('[data-open="true"]'));
  }

  private _onFocusOut = (): void => {
    queueMicrotask(() => { if (this._owner.isConnected && !this.containsInteraction()) { this.hide(); this._onExit(); } });
  };

  private _onOutsideFocus = (): void => {
    if (this._open && !this.containsInteraction()) { this.hide(); this._onExit(); }
  };

  private _onOutsidePointer = (event: Event): void => {
    if (!event.composedPath().includes(this._owner)) this._pendingPointer = true;
  };

  private _finishPointerExit = (): void => {
    if (!this._pendingPointer) return;
    this.hide(); this._onExit();
  };

  private _onOutsideClick = (event: Event): void => {
    if (!event.composedPath().includes(this._owner)) this._finishPointerExit();
  };

  private _onPointerUp = (): void => {
    // Normal clicks commit during capture, with a fixed event path and before
    // Save/Preview runs. A cancelled/dragged pointer still finishes the edit.
    if (this._pendingPointer) this._exitTimer = window.setTimeout(this._finishPointerExit, 0);
  };

  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && event.composedPath().includes(this._toolbar)) {
      // Nested picker dialogs own their Escape handling.
      if (event.composedPath().some(node => node instanceof HTMLElement
        && (node.localName === 'qds-font-picker' || node.localName === 'qds-color-picker'))) return;
      event.preventDefault(); event.stopPropagation();
      this._anchor.querySelector<HTMLElement>('[contenteditable="true"]')?.focus({ preventScroll: true });
      this.hide();
    }
  };

  show = (): void => {
    if (this._open || !this._owner.isConnected || this._owner.hasAttribute('readonly') || this._owner.hasAttribute('disabled')) return;
    this._open = true;
    this._toolbar.setAttribute('data-floating-open', '');
    if (typeof this._toolbar.showPopover === 'function') this._toolbar.showPopover();
    this._position();
    document.addEventListener('pointerdown', this._onOutsidePointer, true);
    document.addEventListener('pointerup', this._onPointerUp, true);
    document.addEventListener('pointercancel', this._finishPointerExit, true);
    document.addEventListener('click', this._onOutsideClick, true);
    document.addEventListener('focusin', this._onOutsideFocus);
    document.addEventListener('scroll', this._schedulePosition, true);
    window.addEventListener('resize', this._schedulePosition);
    window.visualViewport?.addEventListener('resize', this._schedulePosition);
    window.visualViewport?.addEventListener('scroll', this._schedulePosition);
    this._observer = new ResizeObserver(this._schedulePosition);
    this._observer.observe(this._anchor); this._observer.observe(this._toolbar);
  };

  private _schedulePosition = (): void => {
    if (!this._frame && this._open) this._frame = requestAnimationFrame(() => { this._frame = 0; this._position(); });
  };

  private _position(): void {
    const viewport = window.visualViewport;
    const width = viewport?.width || window.innerWidth;
    const height = viewport?.height || window.innerHeight;
    const offsetLeft = viewport?.offsetLeft || 0;
    const offsetTop = viewport?.offsetTop || 0;
    this._toolbar.style.maxWidth = `${Math.max(0, width - 16)}px`;
    const _anchor = this._anchor.getBoundingClientRect();
    const bounds = this._toolbar.getBoundingClientRect();
    this._toolbar.style.left = `${Math.max(offsetLeft + 8, Math.min(_anchor.left, offsetLeft + width - bounds.width - 8))}px`;
    this._toolbar.style.top = `${Math.max(offsetTop + 8, Math.min(_anchor.top - bounds.height - 8, offsetTop + height - bounds.height - 8))}px`;
  }

  hide(): void {
    if (!this._open) return;
    this._open = false;
    if (this._toolbar.isConnected && typeof this._toolbar.hidePopover === 'function') this._toolbar.hidePopover();
    this._toolbar.removeAttribute('data-floating-open');
    document.removeEventListener('pointerdown', this._onOutsidePointer, true);
    document.removeEventListener('pointerup', this._onPointerUp, true);
    document.removeEventListener('pointercancel', this._finishPointerExit, true);
    document.removeEventListener('click', this._onOutsideClick, true);
    this._pendingPointer = false;
    if (this._exitTimer !== undefined) window.clearTimeout(this._exitTimer);
    this._exitTimer = undefined;
    document.removeEventListener('focusin', this._onOutsideFocus);
    document.removeEventListener('scroll', this._schedulePosition, true);
    window.removeEventListener('resize', this._schedulePosition);
    window.visualViewport?.removeEventListener('resize', this._schedulePosition);
    window.visualViewport?.removeEventListener('scroll', this._schedulePosition);
    this._observer?.disconnect(); this._observer = null;
    if (this._frame) cancelAnimationFrame(this._frame);
    this._frame = 0;
  }

  destroy(): void {
    this.hide();
    this._owner.removeEventListener('focusin', this.show);
    this._owner.removeEventListener('focusout', this._onFocusOut);
    this._owner.removeEventListener('keydown', this._onKeyDown);
  }
}

export function deepActiveElement(): Element | null {
  let active = document.activeElement;
  while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
  return active;
}

function containsComposed(_owner: HTMLElement, node: Node | null): boolean {
  while (node) {
    if (node === _owner) return true;
    node = node.parentNode || (node as ShadowRoot).host || null;
  }
  return false;
}
